import { useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { UploadCloud, Check, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import SEO from "@/components/SEO";
import { SquigglyUnderline, UpsettingDog } from "@/components/Doodle";

const MODES = {
  artwork: {
    kind: "artwork",
    seoTitle: "Submit Your Weird Little Drawing",
    seoDescription:
      "Send Wet Cabbage your weirdest hand-drawn art. Wonky frogs, chaotic vegetables, bad dogs — the worse, the better. Featured artists get put on hoodies.",
    kicker: "Community submissions · form 27b/6",
    h1: "Submit your weird little drawing.",
    intro:
      "We want: hand-drawn weirdness, bad dogs, chaotic vegetables, notebook doodles, emotionally unwell frogs. We don't want: polished AI realism, copyrighted characters, offensive stuff, or clipart.",
    dropLabel: "Drop your cursed image here",
    artistPlaceholder: "e.g. Gary the Intern",
    titleLabel: "Artwork title",
    titlePlaceholder: "e.g. Dog That Saw Too Much",
    showLocation: false,
    showFeaturedCheckbox: true,
    successHeadline: "Received. Thank you, weirdo.",
    successCopy:
      "Your drawing is now being reviewed by a mildly qualified human. If it's cursed enough, it'll appear in the Wet Gallery shortly.",
    successCTA: { label: "Visit the gallery", href: "/gallery" },
  },
  sighting: {
    kind: "sighting",
    seoTitle: "Submit a Field Report",
    seoDescription:
      "Spotted a Wet Cabbage subject in the wild? File a field report. Amateur naturalists welcome.",
    kicker: "Wildlife incident log · form WC-FR/02",
    h1: "Submit a Field Report.",
    intro:
      "We want: photos of Wet Cabbage subjects spotted in their natural habitat — train stations, kebab shops, garden centres, pier ends. We do not need: studio shots, weddings, or anything dignified.",
    dropLabel: "Drop your sighting photo here",
    artistPlaceholder: "e.g. Amateur naturalist J. Briggs",
    titleLabel: "Sighting headline",
    titlePlaceholder: "e.g. Subject yelling at a vending machine",
    showLocation: true,
    showFeaturedCheckbox: false,
    successHeadline: "Sighting logged. Specimen catalogued.",
    successCopy:
      "Your report is under review by our amateur naturalists. Approved sightings will appear in Field Reports on the homepage.",
    successCTA: { label: "Back to the homepage", href: "/" },
  },
};

export default function Submit() {
  const [searchParams] = useSearchParams();
  const modeKey = searchParams.get("type") === "sighting" ? "sighting" : "artwork";
  const M = MODES[modeKey];
  const isSighting = modeKey === "sighting";

  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [artist, setArtist] = useState("");
  const [handle, setHandle] = useState("");
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [location, setLocation] = useState("");
  const [agree, setAgree] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const handleFile = (f) => {
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error("Only image files, please (png/jpg/gif/webp).");
      return;
    }
    if (f.size > 10 * 1024 * 1024) {
      toast.error("Max 10MB — compress that beast.");
      return;
    }
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error(isSighting ? "Upload your sighting photo." : "Upload your weird little drawing first.");
    if (!artist.trim()) return toast.error(isSighting ? "We need a reporter name." : "We need an artist name.");
    if (!title.trim()) return toast.error("Give it a title. Even a bad one.");

    const fd = new FormData();
    fd.append("artist_name", artist);
    fd.append("title", title);
    if (handle) fd.append("handle", handle);
    if (desc) fd.append("description", desc);
    fd.append("agree_featured", agree ? "true" : "false");
    fd.append("kind", M.kind);
    if (isSighting && location) fd.append("location", location);
    fd.append("file", file);

    try {
      setSubmitting(true);
      await api.post("/submissions", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setDone(true);
      toast.success(isSighting ? "Sighting logged. Stand by." : "Submitted. Our curator will take a disappointed look.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div
        className="mx-auto flex max-w-[800px] flex-col items-center px-6 py-24 text-center md:px-10"
        data-testid="submit-success"
      >
        <UpsettingDog className="h-40 w-40 wc-wobble" />
        <h1 className="font-display mt-8 text-5xl font-black tracking-tighter sm:text-6xl">
          {M.successHeadline}
        </h1>
        <p className="mt-4 max-w-md">{M.successCopy}</p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <a href={M.successCTA.href} className="wc-btn">
            {M.successCTA.label}
          </a>
          <button
            onClick={() => {
              setFile(null);
              setPreview("");
              setArtist("");
              setHandle("");
              setTitle("");
              setDesc("");
              setLocation("");
              setAgree(false);
              setDone(false);
            }}
            className="wc-btn wc-btn-outline"
            data-testid="submit-again"
          >
            Submit another
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="submit-page">
      <SEO
        title={M.seoTitle}
        path={isSighting ? "/submit?type=sighting" : "/submit"}
        description={M.seoDescription}
      />
      <section className="border-b-2 border-[#1A3626] bg-[#F9F9F4] py-16 md:py-24">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <div className="font-display text-xs font-bold uppercase tracking-[0.3em]">
            {M.kicker}
          </div>
          <h1 className="font-display mt-3 text-5xl font-black leading-[0.9] tracking-tighter sm:text-7xl lg:text-[108px]">
            {M.h1}
          </h1>
          <div className="mt-3 max-w-md">
            <SquigglyUnderline className="h-4 w-full" />
          </div>
          <p className="mt-6 max-w-2xl text-base md:text-lg">{M.intro}</p>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-10 px-6 md:grid-cols-[1.1fr_0.9fr] md:px-10">
          <form
            onSubmit={onSubmit}
            className="space-y-6"
            data-testid="submit-form"
          >
            <div
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                handleFile(e.dataTransfer.files?.[0]);
              }}
              onClick={() => inputRef.current?.click()}
              role="button"
              tabIndex={0}
              className={`relative cursor-pointer border-2 border-dashed border-[#1A3626] p-8 text-center transition-all ${
                dragOver ? "bg-[#93C065]" : "bg-[#EBE7DD]"
              }`}
              data-testid="submit-dropzone"
            >
              {preview ? (
                <div className="flex flex-col items-center gap-4">
                  <img
                    src={preview}
                    alt="preview"
                    className="max-h-64 border-2 border-[#1A3626] bg-[#F9F9F4]"
                  />
                  <div className="font-display text-xs font-bold uppercase tracking-widest">
                    {file?.name}
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-3">
                  <UploadCloud className="h-10 w-10" strokeWidth={2.5} />
                  <div className="font-display text-lg font-bold uppercase tracking-wide">
                    {M.dropLabel}
                  </div>
                  <div className="text-xs uppercase tracking-[0.2em] text-[#1A3626]">
                    PNG · JPG · GIF · WEBP · max 10MB
                  </div>
                </div>
              )}
              <input
                ref={inputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => handleFile(e.target.files?.[0])}
                data-testid="submit-file-input"
              />
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Field
                label={isSighting ? "Reporter name" : "Artist name"}
                value={artist}
                onChange={setArtist}
                placeholder={M.artistPlaceholder}
                required
                testid="artist-name"
              />
              <Field
                label="Instagram / TikTok handle"
                value={handle}
                onChange={setHandle}
                placeholder="@weirdo (optional)"
                testid="handle"
              />
            </div>

            {M.showLocation && (
              <Field
                label="Location of sighting"
                value={location}
                onChange={setLocation}
                placeholder="e.g. Skegness Pier, 16:42"
                testid="location"
              />
            )}

            <Field
              label={M.titleLabel}
              value={title}
              onChange={setTitle}
              placeholder={M.titlePlaceholder}
              required
              testid="title"
            />

            <div>
              <label className="font-display mb-2 block text-xs font-bold uppercase tracking-[0.3em]">
                Description (optional)
              </label>
              <textarea
                value={desc}
                onChange={(e) => setDesc(e.target.value)}
                rows={4}
                className="wc-input"
                placeholder="Give us a funny little backstory…"
                data-testid="description"
              />
            </div>

            {M.showFeaturedCheckbox && (
              <label
                className="flex cursor-pointer items-start gap-3 border-2 border-[#1A3626] bg-[#EBE7DD] p-4"
                data-testid="submit-agree-label"
              >
                <input
                  type="checkbox"
                  className="mt-1 h-5 w-5 shrink-0 accent-[#93C065]"
                  checked={agree}
                  onChange={(e) => setAgree(e.target.checked)}
                  data-testid="submit-agree"
                />
                <span className="text-sm leading-relaxed">
                  I confirm this is my own drawing and I'm okay with Wet Cabbage
                  featuring it in the gallery (and possibly printing it on
                  questionable products).
                </span>
              </label>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="wc-btn w-full md:w-auto"
              data-testid="submit-button"
            >
              {submitting
                ? "Sending..."
                : isSighting
                ? "File the field report"
                : "Send it to the curator"}
            </button>
          </form>

          <aside className="space-y-5">
            <div className="wc-card p-6">
              <div className="font-display text-xs font-bold uppercase tracking-[0.3em] text-[#1A3626]">
                Curatorial standards
              </div>
              <h3 className="font-display mt-2 text-2xl font-black tracking-tight">
                What we love
              </h3>
              <ul className="mt-4 space-y-2 text-sm">
                <Li ok>Hand-drawn art, wonky and proud</Li>
                <Li ok>Weird creatures / bad dogs</Li>
                <Li ok>Chaotic vegetables</Li>
                <Li ok>Emotionally unwell frogs</Li>
                <Li ok>Notebook doodles</Li>
              </ul>
              <h3 className="font-display mt-6 text-2xl font-black tracking-tight">
                What we don't
              </h3>
              <ul className="mt-4 space-y-2 text-sm">
                <Li>Polished AI realism</Li>
                <Li>Offensive / hateful content</Li>
                <Li>Copyrighted characters</Li>
                <Li>Generic clipart</Li>
              </ul>
            </div>

            <div className="wc-card bg-[#0F0F0F] p-6 text-[#F9F9F4]">
              <div className="font-display text-xs font-bold uppercase tracking-[0.3em] text-[#93C065]">
                Possible futures
              </div>
              <h3 className="font-display mt-2 text-2xl font-black tracking-tight">
                Featured artists get put on hoodies.
              </h3>
              <p className="mt-3 text-sm text-[#EBE7DD]">
                Monthly winners selected by the committee (a confused adult with a
                stamp). Revenue-sharing on featured drops. Tiny amounts of fame.
              </p>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, required, testid }) {
  return (
    <div>
      <label className="font-display mb-2 block text-xs font-bold uppercase tracking-[0.3em]">
        {label}
        {required && <span className="ml-1 text-[#D9534F]">*</span>}
      </label>
      <input
        type="text"
        className="wc-input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={`field-${testid}`}
      />
    </div>
  );
}

function Li({ children, ok = false }) {
  return (
    <li className="flex items-start gap-2">
      {ok ? (
        <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#93C065]" strokeWidth={3} />
      ) : (
        <X className="mt-0.5 h-4 w-4 shrink-0 text-[#D9534F]" strokeWidth={3} />
      )}
      <span>{children}</span>
    </li>
  );
}
